// Raising a quality alert: find the defect, assign it, and see what Odoo
// actually recorded.
//
// The design's confirmation screen invented a QA number and a list of people
// who had acknowledged nothing. This shows the real alert: its number, the team
// it went to, and when it is due.
//
// The type picker that used to sit in front of this is now the Raise sheet, and
// the block-work-centre control that sat beside it is gone with the redesign —
// this app no longer opens or closes a downtime record.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { AlertPayload, AlertVM, PodVM, RaisedAlertVM } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Chip } from '@/components/ui/Chip';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState, SectionLabel, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { otherFor, searchDefects, withTypedWords } from '@/lib/defectSearch';
import { downscaleToJpeg } from '@/lib/image';
import { useDictation } from '@/hooks/useDictation';
import { useDefectCategories, useMyAlerts, usePodAlerts, useRaiseAlert, useTeams } from '@/queries';
import { useStore } from '@/store';

type ReportKind = 'quality' | 'safety';

/**
 * Step 2: the alert itself.
 *
 * The team comes first because it decides the shape of everything under it:
 * `use_alert_categories` on the team is what makes category and subcategory
 * appear on Odoo's own form, and a team without it takes a typed title
 * instead. Asking for the defect before the team meant picking a defect the
 * chosen team would then throw away — Odoo clears both category fields on
 * write when the team does not use them.
 *
 * But an operator should not have to answer it. The list is fifteen teams the
 * office uses as categories — lab testing, client defects, NCR list — and the
 * floor's complaint about raising an alert was exactly that list. So a quality
 * alert opens with the deployment's default team already picked (see
 * SF_QA_DEFAULT_TEAM_ID) and the defect search in front; "Change" brings the
 * chooser back for the odd alert that belongs elsewhere. Safety alerts are
 * left to choose, since nothing says which team a safety matter is for.
 *
 * The taxonomy is two levels and both are searchable: a query matches a
 * category by its own name and a defect by either its name or its parent's,
 * so "tile" finds the Tiling category and every defect under it. Tapping a
 * category narrows the list to its defects; tapping a defect is the pick, and
 * the category comes with it.
 */
export function AlertFormSheet({
  open,
  kind,
  pod,
  onClose,
  onRaised,
}: {
  open: boolean;
  kind: ReportKind;
  pod: PodVM | undefined;
  onClose: () => void;
  onRaised: (r: RaisedAlertVM) => void;
}) {
  const t = useT();
  const operator = useStore((s) => s.operator);
  const workcentreId = useStore((s) => s.workcentreId);
  const workcentreName = useStore((s) => s.workcentreName);

  const cats = useDefectCategories();
  const teams = useTeams();
  const raise = useRaiseAlert();

  const qaDefaultTeamId = useStore((s) => s.qaDefaultTeamId);
  const defaultTeamId = kind === 'quality' ? qaDefaultTeamId : null;
  const [teamId, setTeamId] = useState<number | null>(defaultTeamId);
  const [query, setQuery] = useState('');
  /** The top-level category being browsed, or that the picked defect sits under. */
  const [category, setCategory] = useState<{ id: number; name: string } | null>(null);
  const [picked, setPicked] = useState<{ id: number; name: string } | null>(null);
  const [title, setTitle] = useState('');
  // The default is applied as the sheet opens on an empty form — not whenever
  // the team is null, because clearing it is the operator asking for the
  // chooser and the chooser has to stay; and not over a half-filled form,
  // because a team they changed before the Back key closed the sheet is
  // theirs to keep. A default the instance does not know is simply not found
  // in the list, and the chooser shows as if there were none.
  const wasOpen = useRef(open);
  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (opening && teamId == null && !picked && !title.trim()) setTeamId(defaultTeamId);
  }, [open, defaultTeamId, teamId, picked, title]);
  const [priority, setPriority] = useState<'0' | '1' | '2' | '3'>('1');
  const [notes, setNotes] = useState('');
  // Bare base64 JPEGs in the order taken. One at most for an ordinary team;
  // as many as the operator wants for a team that requires before/after
  // pictures, where the first is the floor of the raise.
  const [photos, setPhotos] = useState<string[]>([]);
  const language = useStore((s) => s.language);
  const speechConsent = useStore((s) => s.speechConsent);
  const setSpeechConsent = useStore((s) => s.setSpeechConsent);
  const dictation = useDictation(language);

  const team = (teams.data ?? []).find((tm) => tm.id === teamId) ?? null;
  const usesCategories = team?.useCategories ?? false;
  /**
   * The team insists on a before AND an after picture before an alert
   * closes, so the raise is where the before pictures come from: the photo
   * step is mandatory and multi-shot, and they go into the alert's "before"
   * bucket rather than its chatter. Any other team keeps the one optional
   * photo it always had.
   */
  const mandatoryPhotos = team?.requirePictures ?? false;

  const { parents, leaves } = useMemo(() => {
    const all = cats.data ?? [];
    const byId = new Map(all.map((c) => [c.id, c]));
    const tops = all.filter((c) => c.parentId == null);
    const subs = all
      .filter((c) => c.parentId != null)
      .map((c) => ({ ...c, parentName: byId.get(c.parentId!)?.name ?? '' }));
    const childCount = new Map<number, number>();
    for (const l of subs) childCount.set(l.parentId!, (childCount.get(l.parentId!) ?? 0) + 1);
    return { parents: tops.map((c) => ({ ...c, children: childCount.get(c.id) ?? 0 })), leaves: subs };
  }, [cats.data]);

  // What the list shows: inside a category, its defects; otherwise both layers
  // at once, categories first so a broad match reads as a heading. The match
  // forgives endings, part words and a slip of the thumb (lib/defectSearch).
  const results = useMemo(() => {
    const q = query.trim();
    if (category) {
      const mine = leaves.filter((l) => l.parentId === category.id);
      return q ? { parents: [], leaves: searchDefects([], mine, q, mine.length).leaves } : { parents: [], leaves: mine };
    }
    if (!q) return { parents, leaves: [] };
    return searchDefects(parents, leaves, q);
  }, [parents, leaves, query, category]);

  // Where a problem the list does not have goes: this category's own Other, or
  // the top-level Other's Free text. Offered whenever something is typed, not
  // only when nothing matched — a forgiving search always finds SOMETHING, and
  // "something" is not always the operator's problem.
  const other = useMemo(
    () => (query.trim() ? otherFor(parents, leaves, category?.id ?? null) : null),
    [parents, leaves, category, query],
  );

  const pickLeaf = (leaf: { id: number; name: string; parentId: number | null; parentName: string }) => {
    setPicked({ id: leaf.id, name: leaf.name });
    if (leaf.parentId != null) setCategory({ id: leaf.parentId, name: leaf.parentName });
    setQuery('');
  };
  /** Raise as Other, keeping the words already typed: they become the description. */
  const pickOther = () => {
    if (!other) return;
    const typed = query;
    pickLeaf(other);
    setNotes((n) => withTypedWords(typed, n));
  };
  const changePick = () => {
    setPicked(null);
    setQuery('');
  };
  const chooseTeam = (id: number | null) => {
    setTeamId(id);
    // A defect picked for one team means nothing to a team that does not use
    // categories, and vice versa for a typed title.
    setPicked(null);
    setCategory(null);
    setQuery('');
  };

  const problemChosen = usesCategories ? !!picked : title.trim().length > 0;
  const photosMissing = mandatoryPhotos && photos.length === 0;
  const canSubmit = team != null && problemChosen && !photosMissing && !raise.isPending;

  const addPhoto = async (file: File | undefined) => {
    if (!file) return;
    const jpeg = await downscaleToJpeg(file);
    // A team without the rule keeps one photo, so a second shot is a retake.
    setPhotos((list) => (mandatoryPhotos ? [...list, jpeg] : [jpeg]));
  };
  const removePhoto = (i: number) => setPhotos((list) => list.filter((_, j) => j !== i));

  const submit = () => {
    if (!team || !problemChosen) return;
    const payload: AlertPayload = {
      // Odoo titles a categorised alert "Category - Defect" itself; sending the
      // same keeps the record readable even where that compute does not run.
      title: usesCategories && picked ? [category?.name, picked.name].filter(Boolean).join(' - ') : title.trim(),
      teamId: team.id,
      priority,
      ...(usesCategories && category ? { categoryId: category.id } : {}),
      ...(usesCategories && picked ? { subcategoryId: picked.id } : {}),
      ...(notes.trim() ? { description: notes.trim() } : {}),
      ...(pod?.moId ? { productionId: pod.moId } : {}),
      ...(pod?.woId ? { workorderId: pod.woId } : {}),
      ...(workcentreId ? { workcentreId } : {}),
    };
    raise.mutate(
      { payload, photos, bucket: mandatoryPhotos ? 'before' : 'other' },
      {
        onSuccess: (r) => {
          setTeamId(defaultTeamId);
          setPicked(null);
          setCategory(null);
          setQuery('');
          setTitle('');
          setNotes('');
          setPhotos([]);
          onRaised(r);
        },
      },
    );
  };

  const input: React.CSSProperties = {
    width: '100%',
    height: 44,
    borderRadius: 12,
    background: 'var(--surface2)',
    border: '1.5px solid var(--line)',
    padding: '0 12px',
    font: '500 13.5px Manrope,sans-serif',
    color: 'var(--ink)',
  };
  const row: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 11,
    background: 'var(--surface2)',
    width: '100%',
    textAlign: 'left',
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      variant="tall"
      title={kind === 'quality' ? t.newQualityAlert : t.newSafetyAlert}
      footer={
        <Button
          variant={kind === 'safety' ? 'danger' : 'primary'}
          disabled={!canSubmit}
          pending={raise.isPending}
          onClick={submit}
        >
          {!team ? t.chooseTeamT : !problemChosen ? t.pickIssue : photosMissing ? t.takeBeforePictureT : t.raiseAlert}
        </Button>
      }
    >
      {/* Context Odoo will record automatically — shown so it is not a surprise. */}
      <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        {[
          [t.manufOrder, pod?.mo ?? '—'],
          [t.product, [pod?.proj, pod?.type].filter(Boolean).join(' ') || '—'],
          [t.workcentre, workcentreName ?? '—'],
          [t.raisedByL, operator?.name ?? '—'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
            <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>{k}</span>
            <span style={{ font: '600 11.5px Manrope,sans-serif', textAlign: 'right' }}>{v}</span>
          </div>
        ))}
      </div>

      <SectionLabel>{t.whichTeamT}</SectionLabel>
      {team ? (
        // Folded to the one answer once given, the same way the defect is:
        // the row of every team is a question, and a question already
        // answered should not go on taking the space of one.
        <Picked
          title={team.name}
          sub={usesCategories ? `${t.category} · ${t.subcategory}` : t.whatsWrongT}
          onChange={() => chooseTeam(null)}
          changeLabel={t.change}
        />
      ) : teams.isPending ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Skeleton height={44} radius={22} />
          <Skeleton height={44} radius={22} />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {(teams.data ?? []).map((tm) => (
            <Chip key={tm.id} kind="select" selected={false} onClick={() => chooseTeam(tm.id)}>
              {tm.name}
            </Chip>
          ))}
        </div>
      )}

      {team && !usesCategories && (
        <>
          <SectionLabel>{t.whatsWrongT}</SectionLabel>
          <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 8px' }}>{t.noCategoriesTeamT}</p>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t.describeProblemT}
            aria-label={t.whatsWrongT}
            maxLength={200}
            style={{ ...input, marginBottom: 16 }}
          />
        </>
      )}

      {team && usesCategories && (
        <>
          <SectionLabel>{t.findProblem}</SectionLabel>
          {picked ? (
            <Picked title={picked.name} sub={category?.name ?? ''} onChange={changePick} changeLabel={t.change} />
          ) : (
            <>
              {category && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <button
                    type="button"
                    className="press"
                    onClick={() => {
                      setCategory(null);
                      setQuery('');
                    }}
                    style={{ font: '600 12px Manrope,sans-serif', color: 'var(--accent)', minHeight: 32 }}
                  >
                    ‹ {t.allCategoriesT}
                  </button>
                  <span style={{ font: "600 12.5px 'Space Grotesk',sans-serif" }}>{category.name}</span>
                </div>
              )}
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchDefect}
                aria-label={t.searchDefect}
                style={{ ...input, marginBottom: 10 }}
              />
              {cats.isPending ? (
                <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                  <Skeleton height={44} radius={12} />
                  <Skeleton height={44} radius={12} />
                </div>
              ) : results.parents.length === 0 && results.leaves.length === 0 && !other ? (
                <EmptyState>{t.noMatchingDefectT}</EmptyState>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                  {results.parents.map((c) => (
                    <button
                      key={`c${c.id}`}
                      type="button"
                      className="press-row"
                      onClick={() => {
                        setCategory({ id: c.id, name: c.name });
                        setQuery('');
                      }}
                      style={row}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', font: "600 12.5px 'Space Grotesk',sans-serif" }}>{c.name}</span>
                        <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>
                          {t.category} · {c.children} {t.subcategoriesT}
                        </span>
                      </span>
                      <span aria-hidden="true" style={{ color: 'var(--sub)' }}>›</span>
                    </button>
                  ))}
                  {results.leaves.map((c) => (
                    <button key={`l${c.id}`} type="button" className="press-row" onClick={() => pickLeaf(c)} style={row}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', font: '600 12.5px Manrope,sans-serif' }}>{c.name}</span>
                        {!category && (
                          <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>
                            {c.parentName}
                          </span>
                        )}
                      </span>
                      <span style={{ color: 'var(--sub)' }}>＋</span>
                    </button>
                  ))}
                  {other && (
                    <button
                      type="button"
                      className="press-row"
                      onClick={pickOther}
                      style={{ ...row, background: 'transparent', border: '1.5px dashed var(--line)' }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', font: '600 12.5px Manrope,sans-serif' }}>{t.notListedT}</span>
                        <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>
                          “{query.trim()}” {t.typedGoesInNoteT} · {other.parentName}
                        </span>
                      </span>
                      <span style={{ color: 'var(--sub)' }}>＋</span>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {team && problemChosen && (
        <>
          <SectionLabel>{t.priority}</SectionLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {(
              [
                ['0', t.low],
                ['1', t.normal],
                ['2', t.high],
                ['3', t.urgent],
              ] as const
            ).map(([v, label]) => (
              <Chip
                key={v}
                kind="select"
                tone={v >= '2' ? 'danger' : 'accent'}
                selected={priority === v}
                onClick={() => setPriority(v)}
              >
                {label}
              </Chip>
            ))}
          </div>

          <SectionLabel>{mandatoryPhotos ? t.beforePicturesAtLeastOneT : t.attach}</SectionLabel>
          {photos.length > 0 && (
            // What is about to be sent, at the size it will be sent. A tick
            // saying "captured" was taken on trust; a thumb over the lens or
            // the wrong pod was only found out in Odoo. One photo shows large;
            // a set shows as a strip, each with its own Remove.
            <div
              style={
                photos.length === 1
                  ? { marginBottom: 8 }
                  : { display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 8, paddingBottom: 2 }
              }
            >
              {photos.map((jpeg, i) => (
                <div
                  key={`${i}-${jpeg.length}`}
                  style={{
                    position: 'relative',
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: '1.5px solid var(--accent)',
                    background: 'var(--surface2)',
                    ...(photos.length === 1 ? {} : { flex: 'none', width: 120, height: 90 }),
                  }}
                >
                  <img
                    src={`data:image/jpeg;base64,${jpeg}`}
                    alt={`${t.photoCaptured} ${i + 1}`}
                    style={
                      photos.length === 1
                        ? { display: 'block', width: '100%', maxHeight: 240, objectFit: 'contain' }
                        : { display: 'block', width: '100%', height: '100%', objectFit: 'cover' }
                    }
                  />
                  <button
                    type="button"
                    className="press"
                    onClick={() => removePhoto(i)}
                    aria-label={`${t.removeT} ${i + 1}`}
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      minHeight: 32,
                      padding: '0 10px',
                      borderRadius: 999,
                      background: 'rgba(22,24,28,.72)',
                      color: '#fff',
                      font: '600 11.5px Manrope,sans-serif',
                    }}
                  >
                    ✕ {photos.length === 1 ? t.removeT : ''}
                  </button>
                </div>
              ))}
            </div>
          )}
          <label
            className="press"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 46,
              borderRadius: 12,
              background: 'var(--surface2)',
              border: `1.5px dashed ${photosMissing ? 'var(--accent)' : 'var(--line)'}`,
              font: '600 12.5px Manrope,sans-serif',
              marginBottom: 16,
              cursor: 'pointer',
            }}
          >
            <Icon name="camera" size={17} />
            {photos.length === 0 ? t.takePhoto : mandatoryPhotos ? t.addPhotoT : t.retakePhoto}
            {/* capture="environment" sends the tap straight to the rear camera,
                not the gallery — the before picture is of the pod in front of
                the operator, now. */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={async (e) => {
                await addPhoto(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>

          <SectionLabel>{t.addNote}</SectionLabel>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t.describeSaw}
            aria-label={t.addNote}
            style={{ ...input, height: 'auto', minHeight: 72, padding: 12, resize: 'vertical' }}
          />

          {dictation.supported && (
            <div style={{ marginTop: 8 }}>
              {!speechConsent ? (
                <button
                  type="button"
                  className="press"
                  onClick={() => setSpeechConsent(true)}
                  style={{
                    width: '100%',
                    minHeight: 44,
                    borderRadius: 12,
                    background: 'var(--surface2)',
                    border: '1.5px solid var(--line)',
                    font: '500 11.5px Manrope,sans-serif',
                    color: 'var(--sub)',
                    padding: '8px 12px',
                    textAlign: 'left',
                  }}
                >
                  {t.voiceNote} — {t.voiceConsentAsk}
                </button>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant={dictation.listening ? 'danger' : 'surface'}
                      size="md"
                      onClick={() => {
                        if (dictation.listening) {
                          dictation.stop();
                          // The transcript is a draft: it goes into the field
                          // for correction, never straight to Odoo.
                          if (dictation.transcript) {
                            setNotes((n) => (n ? `${n} ${dictation.transcript}` : dictation.transcript));
                          }
                        } else {
                          dictation.start();
                        }
                      }}
                      icon={<Icon name={dictation.listening ? 'hazard' : 'mic'} size={16} />}
                    >
                      {dictation.listening ? t.stopT : t.voiceNote}
                    </Button>
                  </div>
                  {dictation.listening && (
                    <p
                      aria-live="polite"
                      style={{
                        font: '500 11.5px Manrope,sans-serif',
                        color: 'var(--sub)',
                        background: 'var(--surface2)',
                        borderRadius: 10,
                        padding: 10,
                        margin: '8px 0 0',
                        minHeight: 20,
                      }}
                    >
                      {dictation.transcript || `${t.voiceNote}…`}
                    </p>
                  )}
                  {dictation.error && (
                    <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--danger)', margin: '8px 0 0' }}>
                      {dictation.error}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}

/**
 * An answer already given — the team, the defect — folded to one line with a
 * way to change it. Both steps use it so the form reads as a list of answers
 * rather than a form still half open.
 */
function Picked({
  title,
  sub,
  onChange,
  changeLabel,
}: {
  title: string;
  sub: string;
  onChange: () => void;
  changeLabel: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'var(--soft)',
        border: '1.5px solid var(--accent)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 16,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: '600 13px Manrope,sans-serif' }}>{title}</span>
        {sub && (
          <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)' }}>{sub}</span>
        )}
      </span>
      <button
        type="button"
        className="press"
        onClick={onChange}
        style={{ font: '600 12px Manrope,sans-serif', color: 'var(--accent)', minHeight: 32, padding: '0 6px' }}
      >
        {changeLabel}
      </button>
    </div>
  );
}

/** Step 3: what Odoo recorded. Real number, real team, real deadline. */
export function RaisedSheet({
  open,
  raised,
  kind,
  onClose,
}: {
  open: boolean;
  raised: RaisedAlertVM | null;
  kind: ReportKind;
  onClose: () => void;
}) {
  const t = useT();
  if (!raised) return null;
  return (
    <Sheet open={open} onClose={onClose} title={kind === 'quality' ? t.qualityRaised : t.safetyRaised} hideTitle>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h2 style={{ font: "700 19px 'Space Grotesk',sans-serif", margin: 0, color: `var(--${kind === 'safety' ? 'danger' : 'warn'})` }}>
          {kind === 'quality' ? t.qualityRaised : t.safetyRaised}
        </h2>
        <span
          style={{
            font: '700 9px Manrope,sans-serif',
            letterSpacing: '.06em',
            padding: '3px 7px',
            borderRadius: 6,
            background: 'var(--soft)',
            color: 'var(--accent)',
            animation: 'ipPulse 2s infinite',
          }}
        >
          ● {t.newTag}
        </span>
      </div>
      <p style={{ font: "600 15px 'Space Grotesk',sans-serif", margin: '0 0 4px' }}>{raised.qa || '—'}</p>
      <p style={{ font: '500 13px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 18px' }}>{raised.title}</p>

      <SectionLabel>{t.whatHappensNext}</SectionLabel>
      <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 12, marginBottom: 18 }}>
        {[
          [t.assignedTo, raised.team || '—'],
          [t.statusT, raised.stage || '—'],
          [t.dueBy, raised.deadline ? raised.deadline.slice(0, 10) : '—'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
            <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>{k}</span>
            <span style={{ font: '600 11.5px Manrope,sans-serif' }}>{v}</span>
          </div>
        ))}
      </div>

      <Button variant="ink" onClick={onClose}>
        {t.backToWork}
      </Button>
    </Sheet>
  );
}

/** Open quality issues on this pod. */
export function IssuesSheet({
  open,
  pod,
  initialView = 'pod',
  onClose,
  onOpenAlert,
}: {
  open: boolean;
  pod: PodVM | undefined;
  /** Which tab to land on — "mine" when opened from settings. */
  initialView?: 'pod' | 'mine';
  onClose: () => void;
  /** A row opens the alert's own sheet — every alert on the pod, whoever raised it. */
  onOpenAlert: (alertId: number) => void;
}) {
  const t = useT();
  const [view, setView] = useState<'pod' | 'mine'>(initialView);
  useEffect(() => {
    if (open) setView(initialView);
  }, [open, initialView]);

  const podAlerts = usePodAlerts(pod?.moId ?? null);
  // Only fetched once that tab is actually looked at.
  const myAlerts = useMyAlerts(open && view === 'mine');
  const active = view === 'mine' ? myAlerts : podAlerts;
  const list = active.data ?? [];

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.qualityIssuesTitle}>
      <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(
          [
            ['pod', t.thisPodTab],
            ['mine', t.raisedByMe],
          ] as const
        ).map(([key, labelText]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className="press"
            onClick={() => setView(key)}
            style={{
              flex: 1,
              minHeight: 44,
              borderRadius: 12,
              font: '600 12.5px Manrope,sans-serif',
              background: view === key ? 'var(--accent)' : 'var(--surface2)',
              color: view === key ? 'var(--accentInk)' : 'var(--ink)',
            }}
          >
            {labelText}
          </button>
        ))}
      </div>

      {active.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={70} />
          <Skeleton height={70} />
        </div>
      ) : active.isError ? (
        <Banner tone="danger" action={{ label: t.retry, onClick: () => active.refetch() }}>
          {(active.error as Error).message}
        </Banner>
      ) : list.length === 0 ? (
        <EmptyState>{view === 'mine' ? t.noOpenIssues : t.noOpenThisPod}</EmptyState>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((a) => (
            <AlertRow key={a.id} alert={a} showMo={view === 'mine'} onOpen={() => onOpenAlert(a.id)} />
          ))}
        </div>
      )}
    </Sheet>
  );
}

/**
 * One alert. Tapping it opens the alert's sheet — stage, defect, photos, notes
 * and, for an approver signed in as themselves, the close. Every alert on the
 * pod is the floor's to look at and work on, not only the ones this operator
 * raised.
 */
function AlertRow({ alert: a, showMo, onOpen }: { alert: AlertVM; showMo: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onOpen}
      style={{ width: '100%', textAlign: 'left', padding: 12, minHeight: 44, background: 'var(--surface2)', borderRadius: 13 }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ font: "600 11.5px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>
          {[a.qa, showMo ? a.mo : null].filter(Boolean).join(' · ')}
        </span>
        <Chip kind="stage" tone={a.stage === 'New' ? 'accent' : 'warn'} style={{ marginLeft: 'auto' }}>
          {a.stage}
        </Chip>
      </span>
      <span style={{ display: 'block', font: '600 13px Manrope,sans-serif' }}>{a.title}</span>
      <span style={{ display: 'block', font: '500 10.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 3 }}>
        {[a.team, a.employeeName, a.age].filter(Boolean).join(' · ')}
        {a.preventMoSignoff ? ' · blocks sign-off' : ''}
      </span>
    </button>
  );
}
